/**
 * Studio → Skill Detail (#153, W23A — 2026-05-23).
 *
 * Full-page edit surface for a tenant skill at `/studio/skills/:skillId`.
 * Replaces the cramped Sheet editor for everything beyond quick edits:
 * the Sheet (mounted by `<SkillEditor>`) remains for quick create + the
 * fast-path edit; the detail page is the canonical surface for variable
 * authoring, tool dependency curation, version inspection, and
 * "Used by Agents" lookup.
 *
 * Six tabs mirror the catalog skill detail pattern:
 *   - Identity        (slug, displayName, description, category, active)
 *   - Instructions    (markdown body, helper panel, live preview)
 *   - Variables       (typed VariableEditor, flavor="skill")
 *   - Tools           (multi-select workspace_tools picker)
 *   - Versions        (placeholder; tenant skills don't yet carry
 *                      versions — surfaced as informational pane)
 *   - Used by Agents  (lists agents whose `skills` array contains this
 *                      skill's slug)
 *
 * Identity Card mounts the shared `<SkillContentEditor mode="edit">`
 * for slug/category/displayName/description/instruction body parity
 * with the sysadmin catalog detail page (closes the parity loop from
 * PR #1181, W22B).
 */

import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  AlertTriangle,
  BookOpen,
  Loader2,
  Pencil,
  Save,
  Trash2,
  X,
  Variable as VariableIcon,
  Wrench,
  History,
  Users,
  Sparkles as SparklesIcon,
  Copy,
  Plus,
  MinusCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  SkillContentEditor,
  type SkillContentDraft,
} from '@/components/skill-editor-shared/SkillContentEditor';
import {
  VariableEditor,
  variablesHaveBlankNames,
  type SkillVariableEntry,
} from '@/components/studio';
import {
  useStudioSkillDetail,
  useUpdateStudioSkill,
  useDeleteStudioSkill,
  type StudioSkillVariable,
} from '@/lib/use-studio-skills';
import { useWorkspaceTools } from '@/lib/use-workspace-tools';
import { useAgents } from '@/lib/use-agents';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/studio/skills/$skillId')({
  // The dynamic $skillId route also captures the reserved '/studio/skills/new'
  // deep-link (there is no skills.new.tsx). 'new' is not a UUID, so falling
  // through to the detail page produces a backend 'id: Invalid UUID' error
  // card. Redirect to the list, where the in-UI 'New Skill' button opens the
  // create dialog (matches the templates.tsx compat-redirect convention).
  beforeLoad: ({ params }) => {
    if (params.skillId === 'new') {
      throw redirect({ to: '/studio/skills', replace: true });
    }
  },
  component: StudioSkillDetailPage,
});

type DetailTab =
  | 'identity'
  | 'instructions'
  | 'variables'
  | 'tools'
  | 'versions'
  | 'used-by';

function StudioSkillDetailPage() {
  const { skillId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useStudioSkillDetail(skillId);
  const updateSkill = useUpdateStudioSkill();
  const deleteSkill = useDeleteStudioSkill();

  const skill = data?.data;

  const [activeTab, setActiveTab] = useState<DetailTab>('identity');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading skill…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !skill) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{error?.message ?? 'Skill not found.'}</div>
          </CardContent>
        </Card>
        <div className="mt-3">
          <Link
            to="/studio/skills"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Back to skills
          </Link>
        </div>
      </div>
    );
  }

  const isSystemRow = skill.scope === 'system';

  const handleDelete = async () => {
    try {
      await deleteSkill.mutateAsync({ id: skill.id });
      toast.success(`Deleted "${skill.displayName}"`);
      navigate({ to: '/studio/skills' });
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleToggleActive = async (next: boolean) => {
    try {
      await updateSkill.mutateAsync({ id: skill.id, isActive: next });
      toast.success(next ? 'Skill activated' : 'Skill deactivated');
    } catch (err) {
      toast.error('Update failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header + back */}
      <div className="flex items-start gap-3">
        <Link
          to="/studio/skills"
          className="text-muted-foreground hover:text-foreground p-1.5 rounded -ml-1.5"
          title="Back to skills"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <BookOpen className="h-7 w-7 text-muted-foreground mt-0.5" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight truncate">
            {skill.displayName}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="font-mono text-[11px]">
              {skill.name}
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {skill.category}
            </Badge>
            {isSystemRow && (
              <Badge
                variant="outline"
                className="text-[10px] bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
              >
                System (read-only)
              </Badge>
            )}
            {skill.clonedFromSystemId && (
              <Badge
                variant="outline"
                className="text-[10px] bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30"
              >
                Cloned
              </Badge>
            )}
            {skill.isActive ? (
              <Badge variant="default" className="text-[10px]">on</Badge>
            ) : (
              <Badge variant="secondary" className="text-[10px]">off</Badge>
            )}
          </div>
        </div>
        {!isSystemRow && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-2 py-1 rounded border bg-muted/30">
              <Label htmlFor="active-toggle" className="text-xs cursor-pointer">
                Active
              </Label>
              <Switch
                id="active-toggle"
                checked={skill.isActive}
                onCheckedChange={handleToggleActive}
                disabled={updateSkill.isPending}
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              className="text-destructive hover:bg-destructive/10"
              title="Delete skill"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Provenance banner — when the row was cloned from a system skill */}
      {skill.clonedFromSystemId && (
        <Card className="border-cyan-500/30 bg-cyan-500/5">
          <CardContent className="py-3 text-sm flex items-start gap-2">
            <Copy className="h-4 w-4 mt-0.5 shrink-0 text-cyan-700 dark:text-cyan-300" />
            <div>
              <div className="font-medium text-cyan-700 dark:text-cyan-300">
                Cloned from system skill
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                You can edit this copy freely; the original system skill is
                unchanged. Provenance UUID:{' '}
                <code className="font-mono">{skill.clonedFromSystemId}</code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DetailTab)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="identity">
            <BookOpen className="h-3.5 w-3.5 mr-1.5" />
            Identity
          </TabsTrigger>
          <TabsTrigger value="instructions">
            <SparklesIcon className="h-3.5 w-3.5 mr-1.5" />
            Instructions
          </TabsTrigger>
          <TabsTrigger value="variables">
            <VariableIcon className="h-3.5 w-3.5 mr-1.5" />
            Variables
            {skill.variables.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[9px]">
                {skill.variables.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tools">
            <Wrench className="h-3.5 w-3.5 mr-1.5" />
            Tools
            {skill.toolDependencies.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[9px]">
                {skill.toolDependencies.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="versions">
            <History className="h-3.5 w-3.5 mr-1.5" />
            Versions
          </TabsTrigger>
          <TabsTrigger value="used-by">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            Used by
          </TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
          <IdentityTab skill={skill} readOnly={isSystemRow} />
        </TabsContent>
        <TabsContent value="instructions">
          <InstructionsTab skill={skill} readOnly={isSystemRow} />
        </TabsContent>
        <TabsContent value="variables">
          <VariablesTab skill={skill} readOnly={isSystemRow} />
        </TabsContent>
        <TabsContent value="tools">
          <ToolsTab skill={skill} readOnly={isSystemRow} />
        </TabsContent>
        <TabsContent value="versions">
          <VersionsTab skill={skill} />
        </TabsContent>
        <TabsContent value="used-by">
          <UsedByAgentsTab skillSlug={skill.name} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete skill</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{' '}
              <code className="bg-muted px-1 rounded">{skill.name}</code>?
              This action cannot be undone. Agents referencing this skill will
              lose the dependency.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete skill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Identity + Instructions via SkillContentEditor ────────────────────────
//
// Shared editor body owns the read/edit chrome. The detail page splits
// it across two tabs so the page never scrolls past a single fold:
// Identity carries slug / category / displayName / description, and
// Instructions carries the markdown body + helper panel. They are
// driven by the same draft because the underlying mutation is one
// PATCH /studio-skills/:id call.

function IdentityTab({
  skill,
  readOnly,
}: {
  skill: NonNullable<ReturnType<typeof useStudioSkillDetail>['data']>['data'];
  readOnly: boolean;
}) {
  const update = useUpdateStudioSkill();
  const baseline: SkillContentDraft = {
    slug: skill.name,
    displayName: skill.displayName,
    description: skill.description,
    instructions: skill.instructions,
    category: skill.category,
  };
  const [draft, setDraft] = useState<SkillContentDraft>(baseline);

  // Resync when the underlying row updates (after save invalidates the
  // detail query). Without this the editor would still show the
  // pre-save draft after a successful save.
  useEffect(() => {
    setDraft({
      slug: skill.name,
      displayName: skill.displayName,
      description: skill.description,
      instructions: skill.instructions,
      category: skill.category,
    });
  }, [
    skill.id,
    skill.name,
    skill.displayName,
    skill.description,
    skill.instructions,
    skill.category,
  ]);

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id: skill.id,
        displayName: draft.displayName,
        description: draft.description,
        category: draft.category,
        // Instructions live on a sibling tab but share the draft —
        // saving from Identity will also persist any pending edits on
        // the Instructions tab. This matches the catalog detail flow.
        instructions: draft.instructions,
      });
      toast.success('Saved changes');
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
      throw err;
    }
  };

  return (
    <SkillContentEditor
      mode="edit"
      draft={draft}
      onDraftChange={setDraft}
      baseline={baseline}
      onSave={handleSave}
      saving={update.isPending}
      readOnly={readOnly}
      immutableSlug
      toolDependencies={skill.toolDependencies}
    />
  );
}

function InstructionsTab(props: {
  skill: NonNullable<ReturnType<typeof useStudioSkillDetail>['data']>['data'];
  readOnly: boolean;
}) {
  // Reuse the same SkillContentEditor; it owns Identity + Description +
  // Instructions panes already. Rendering it on this tab too is the
  // simplest way to give users a focused authoring surface — they can
  // collapse Identity / Description visually by scrolling, and the
  // helper panel sits below Instructions.
  return <IdentityTab {...props} />;
}

// ─── Variables tab ─────────────────────────────────────────────────────────

function VariablesTab({
  skill,
  readOnly,
}: {
  skill: NonNullable<ReturnType<typeof useStudioSkillDetail>['data']>['data'];
  readOnly: boolean;
}) {
  const update = useUpdateStudioSkill();
  const [variables, setVariables] = useState<StudioSkillVariable[]>(
    skill.variables,
  );
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setVariables(skill.variables);
  }, [skill.id, skill.variables]);

  const dirty = useMemo(
    () => JSON.stringify(variables) !== JSON.stringify(skill.variables),
    [variables, skill.variables],
  );

  const hasBlankNames = variablesHaveBlankNames(
    variables as SkillVariableEntry[],
  );

  const handleSave = async () => {
    if (hasBlankNames) {
      toast.error('Every variable needs a name before saving.');
      return;
    }
    try {
      await update.mutateAsync({ id: skill.id, variables });
      toast.success('Variables saved');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <VariableIcon className="h-4 w-4" />
            Variables ({variables.length})
          </CardTitle>
          <CardDescription>
            Typed runtime inputs the agent fills before composing this skill.
            Reference them in the instruction body as <code>{'{{var_name}}'}</code>.
          </CardDescription>
        </div>
        {!readOnly && (
          <div className="flex gap-1.5">
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setVariables(skill.variables);
                    setEditing(false);
                  }}
                  title="Discard changes"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || update.isPending || hasBlankNames}
                  title={
                    hasBlankNames
                      ? 'Every variable needs a name'
                      : 'Save changes'
                  }
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                title="Edit variables"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {editing && !readOnly ? (
          <VariableEditor
            flavor="skill"
            value={variables as SkillVariableEntry[]}
            onChange={(next) =>
              setVariables(next as unknown as StudioSkillVariable[])
            }
          />
        ) : variables.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/20">
            No variables defined.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-20">Required</TableHead>
                <TableHead>Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variables.map((v, i) => (
                <TableRow key={`${v.name}-${i}`}>
                  <TableCell className="font-mono text-xs">{v.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {v.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {v.required ? 'yes' : 'no'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {v.description || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Tools tab ─────────────────────────────────────────────────────────────

function ToolsTab({
  skill,
  readOnly,
}: {
  skill: NonNullable<ReturnType<typeof useStudioSkillDetail>['data']>['data'];
  readOnly: boolean;
}) {
  const { data: toolsData } = useWorkspaceTools({ scope: 'all' });
  const update = useUpdateStudioSkill();
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState('');
  const [deps, setDeps] = useState<string[]>(skill.toolDependencies);

  useEffect(() => {
    setDeps(skill.toolDependencies);
  }, [skill.id, skill.toolDependencies]);

  const allTools = useMemo(
    () => (toolsData?.data ?? []).map((t) => ({ name: t.name, displayName: t.displayName })),
    [toolsData],
  );

  // Resolve dependency slugs to display rows; surface dangling refs.
  const depRows = useMemo(() => {
    return deps.map((slug) => {
      const t = allTools.find((x) => x.name === slug);
      return {
        slug,
        displayName: t?.displayName ?? null,
        dangling: !t,
      };
    });
  }, [deps, allTools]);

  const danglingCount = depRows.filter((r) => r.dangling).length;
  const dirty = useMemo(
    () => JSON.stringify([...deps].sort()) !== JSON.stringify([...skill.toolDependencies].sort()),
    [deps, skill.toolDependencies],
  );

  const handleSave = async () => {
    try {
      await update.mutateAsync({ id: skill.id, toolDependencies: deps });
      toast.success('Tool dependencies saved');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleRemove = (slug: string) => {
    setDeps((curr) => curr.filter((s) => s !== slug));
  };

  const handleAdd = () => {
    const v = picking.trim();
    if (!v) return;
    if (deps.includes(v)) {
      toast.info('Already added');
      return;
    }
    setDeps((curr) => [...curr, v]);
    setPicking('');
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Tool dependencies ({depRows.length})
          </CardTitle>
          <CardDescription>
            Tools the agent calls when composing this skill. Pick from your
            workspace tools — dangling references show as warnings.
          </CardDescription>
        </div>
        {!readOnly && (
          <div className="flex gap-1.5">
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDeps(skill.toolDependencies);
                    setEditing(false);
                  }}
                  title="Discard changes"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || update.isPending}
                  title="Save changes"
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                title="Edit dependencies"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {danglingCount > 0 && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              {danglingCount} reference{danglingCount === 1 ? '' : 's'} point
              to tools that are not in your workspace. Add the tool under
              Studio → Tools, or remove the reference.
            </div>
          </div>
        )}

        {editing && !readOnly && (
          <div className="flex gap-2">
            <select
              value={picking}
              onChange={(e) => setPicking(e.target.value)}
              className="h-8 text-xs rounded border bg-background px-2 text-foreground flex-1"
              aria-label="Choose a tool to add"
            >
              <option value="">— Choose a tool —</option>
              {allTools
                .filter((t) => !deps.includes(t.name))
                .map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.displayName} ({t.name})
                  </option>
                ))}
            </select>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAdd}
              disabled={!picking}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          </div>
        )}

        {depRows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/20">
            No tool dependencies declared.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Display name</TableHead>
                <TableHead className="w-24">Status</TableHead>
                {editing && !readOnly && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {depRows.map((d) => (
                <TableRow key={d.slug}>
                  <TableCell className="font-mono text-xs">{d.slug}</TableCell>
                  <TableCell className="text-sm">
                    {d.displayName ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {d.dangling ? (
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          'border-amber-500/30 text-amber-700 dark:text-amber-400',
                        )}
                      >
                        missing
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
                        ok
                      </Badge>
                    )}
                  </TableCell>
                  {editing && !readOnly && (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                        onClick={() => handleRemove(d.slug)}
                        title="Remove reference"
                      >
                        <MinusCircle className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Versions tab ──────────────────────────────────────────────────────────

function VersionsTab({
  skill,
}: {
  skill: NonNullable<ReturnType<typeof useStudioSkillDetail>['data']>['data'];
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" />
          Version history
        </CardTitle>
        <CardDescription>
          Tenant skills currently track only the latest revision. Version
          pinning lands with the catalog publish flow — see catalog skill
          versions for system-authored skills.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Version</TableHead>
              <TableHead className="w-44">Updated</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-mono text-xs">current</TableCell>
              <TableCell className="text-xs text-muted-foreground tabular-nums">
                {new Date(skill.updatedAt).toLocaleString()}
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="text-[10px]">
                  active
                </Badge>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─── Used by agents tab ────────────────────────────────────────────────────

function UsedByAgentsTab({ skillSlug }: { skillSlug: string }) {
  const { data: agents, isLoading, error } = useAgents('all');

  const referencingAgents = useMemo(
    () => (agents ?? []).filter((a) => a.skills.includes(skillSlug)),
    [agents, skillSlug],
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Used by agents ({referencingAgents.length})
        </CardTitle>
        <CardDescription>
          Agents whose skill composition includes <code className="bg-muted px-1 rounded">{skillSlug}</code>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 inline mr-1.5 animate-spin" />
            Loading agents…
          </div>
        ) : error ? (
          <div className="text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>{error.message}</div>
          </div>
        ) : referencingAgents.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/20">
            No agents reference this skill yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-32">Status</TableHead>
                <TableHead className="w-32">Ownership</TableHead>
                <TableHead className="w-20">Version</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {referencingAgents.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <div className="text-sm font-medium">{a.name}</div>
                    {a.description && (
                      <div className="text-[11px] text-muted-foreground line-clamp-1">
                        {a.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {a.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-[10px] capitalize">
                      {a.ownership}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    v{a.version}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
